import React, {useCallback} from 'react';
import clsx from 'clsx';
import {faTimes as TimesIcon} from '@netx/core-icons';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import {InputForm} from '@netx/core-components/Form';
import {IconButton} from '@netx/core-components/IconButton';
import type {Theme} from '@netx/core-components/Theme';
import {compareProps} from '@netx/core-next/utils/react';
import {i18n} from '@netx/core/i18n';
import DragHandle from './ItemManagerSortableHandle';

const styles = ({palette, spacing}: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			alignItems: 'center',
			WebkitUserSelect: 'none',
			WebkitTouchCallout: 'none',
			userSelect: 'none',
			'&:not($dense)': {
				margin: spacing(0.5, 0),
				padding: spacing(1),
			},
			'&$dense': {
				padding: spacing(0.5),
			},
			'&.dragging-item': {
				background: palette.custom.background.light,
				opacity: 0.95,
				zIndex: 99999,
				'& button': {
					pointerEvents: 'none',
				},
			},
			'&:not(.sortable)': {
				position: 'absolute',
				bottom: 0,
				left: 0,
				right: 0,
				margin: 0,
				transform: 'none !important',
				zIndex: 2,
			},
		},
		actions: {
			marginLeft: 8,
		},
		dense: {},
	});

interface ChildProps extends Record<string, any> {
	id: number | string;
	errors: any;
	form: InputForm;
	values: any;
	actions: any;
}

export interface ItemManagerItemProps extends ChildProps {
	children(props: ChildProps): React.ReactNode | React.ReactNode[];
	dense?: boolean;
	hideRemove?: boolean;
	onRemoveClick(id: number | string): void;
	sortable?: boolean;
	isSorting?: boolean;
}
const ItemManagerItem: React.FunctionComponent<
	WithStyles<typeof styles> & ItemManagerItemProps
> = ({
	children,
	dense,
	classes,
	hideRemove,
	isSorting,
	onRemoveClick,
	sortable,
	...props
}) => {
	const handleRemoveClick = useCallback(() => {
		onRemoveClick(props.id);
	}, [onRemoveClick, props.id]);

	return (
		<div
			aria-grabbed={isSorting}
			data-testid="item-manager-item"
			className={clsx(classes.root, {
				sortable,
				[classes.dense]: dense,
			})}
		>
			{/* @ts-ignore */}
			{!sortable ? null : <DragHandle isSorting={isSorting} />}
			{children(props)}
			{hideRemove !== true ? (
				<div className={classes.actions}>
					<IconButton
						data-testid="remove-item"
						icon={TimesIcon}
						label={i18n.t('i18n.remove')}
						onClick={handleRemoveClick}
					/>
				</div>
			) : null}
		</div>
	);
};

export default withStyles(styles)(
	React.memo(ItemManagerItem, compareProps([], 'item-manager-item')),
);
